import financialApi from "api/requests/financial";
import Loader from "components/_UI/Loader";
import Text from "components/_UI/Text";
import currency from "helpers/currency";
import React, { useEffect, useMemo, useState } from "react";
import ReactCalendar from "react-calendar";
import "react-calendar/dist/Calendar.css";
import { Calendar, CheckCircle, Clock } from "react-feather";
import styled from "styled-components";
import { FullyCentered } from "styled/alignment/Center";
import { Column } from "styled/alignment/Column";
import { Row } from "styled/alignment/Row";
import breakpoints from "theme/breakpoints";
import colors from "theme/colors";
import fonts from "theme/fonts";

export default function ReceiptsCalendar() {
  const today = new Date();
  const [currentDate, setCurrentDate] = useState(today);
  const [selectedDate, setSelectedDate] = useState(null);
  const [content, setContent] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    getReceiptSchedule();
  }, [currentDate]);

  async function getReceiptSchedule() {
    setLoading(true);
    try {
      const { data } = await financialApi.receiptSchedule(
        currentDate.getMonth() + 1,
        currentDate.getFullYear()
      );
      setContent(data);
    } catch (error) {
    } finally {
      setLoading(false);
    }
  }

  const { totalReleased, totalPending } = useMemo(() => {
    if (!content || !content.length) return { totalReleased: 0, totalPending: 0 };

    const todayStr = `${today.getFullYear()}-${String(today.getMonth() + 1).padStart(2, "0")}-${String(today.getDate()).padStart(2, "0")}`;

    return content.reduce(
      (acc, item) => {
        const amount = parseFloat(item.total_amount) || 0;
        if (item.payment_day <= todayStr) {
          acc.totalReleased += amount;
        } else {
          acc.totalPending += amount;
        }
        return acc;
      },
      { totalReleased: 0, totalPending: 0 }
    );
  }, [content]);

  function formatDateString(date) {
    const y = date.getFullYear();
    const m = String(date.getMonth() + 1).padStart(2, "0");
    const d = String(date.getDate()).padStart(2, "0");
    return `${y}-${m}-${d}`;
  }

  return (
    <CalendarContainer>
      <SectionHeader>
        <SectionIcon>
          <Calendar size={18} color={colors.occasionalPurple} />
        </SectionIcon>
        <HeaderContent>
          <Text type="bold" color={colors.nightDark}>
            Agenda de recebimentos
          </Text>
          <Text name="small" color={colors.night}>
            Acompanhe seus recebimentos por dia
          </Text>
        </HeaderContent>
      </SectionHeader>

      {loading ? (
        <FullyCentered height="300px">
          <Loader />
        </FullyCentered>
      ) : (
        <>
          <SummaryRow>
            <SummaryCard>
              <SummaryIconBox color="#e8f5e9">
                <CheckCircle size={18} color={colors.success} />
              </SummaryIconBox>
              <SummaryInfo>
                <SummaryLabel>Liberado</SummaryLabel>
                <SummaryValue color={colors.success}>
                  {currency.format(totalReleased)}
                </SummaryValue>
              </SummaryInfo>
            </SummaryCard>

            <SummaryCard>
              <SummaryIconBox color="#fff3e0">
                <Clock size={18} color={colors.occasionalOrange} />
              </SummaryIconBox>
              <SummaryInfo>
                <SummaryLabel>A liberar</SummaryLabel>
                <SummaryValue color={colors.occasionalOrange}>
                  {currency.format(totalPending)}
                </SummaryValue>
              </SummaryInfo>
            </SummaryCard>
          </SummaryRow>

          <StyledCalendar
            value={currentDate}
            onChange={(date) => setSelectedDate(date)}
            onActiveStartDateChange={({ activeStartDate }) => {
              if (activeStartDate) {
                setCurrentDate(activeStartDate);
              }
            }}
            tileContent={({ date, view }) => {
              if (view !== "month") return null;

              const dateStr = formatDateString(date);
              const event = content?.find(
                (item) => item.payment_day === dateStr
              );

              if (!event) return null;

              const isPast = dateStr <= formatDateString(today);

              return (
                <TileBody>
                  <AmountBadge isPast={isPast}>
                    {currency.format(event.total_amount)}
                  </AmountBadge>
                  <StatusDot isPast={isPast} />
                </TileBody>
              );
            }}
          />

          <Legend>
            <LegendItem>
              <StatusDot isPast />
              <Text name="small" color={colors.night}>Liberado</Text>
            </LegendItem>
            <LegendItem>
              <StatusDot />
              <Text name="small" color={colors.night}>A liberar</Text>
            </LegendItem>
          </Legend>
        </>
      )}
    </CalendarContainer>
  );
}

/* ── Styled Components ─────────────────────────────── */

const CalendarContainer = styled(Column)`
  background: ${colors.neutralLight};
  border: 1px solid ${colors.neutralDark};
  border-radius: 12px;
  padding: 20px 16px;
  margin-top: 10px;

  @media screen and (min-width: ${breakpoints.md}) {
    padding: 28px 28px;
  }
`;

const SectionHeader = styled.div`
  display: flex;
  align-items: flex-start;
  gap: 8px;
  margin-bottom: 16px;
`;

const SectionIcon = styled.div`
  display: flex;
  align-items: center;
  justify-content: center;
  width: 32px;
  height: 32px;
  border-radius: 8px;
  background: #F3EAFF;
  flex-shrink: 0;
  margin-top: 2px;
`;

const HeaderContent = styled.div`
  flex: 1;
`;

/* ── Summary Row ───────────────────────────────────── */

const SummaryRow = styled(Row)`
  gap: 12px;
  margin-bottom: 20px;
  flex-wrap: wrap;
`;

const SummaryCard = styled.div`
  display: flex;
  align-items: center;
  gap: 10px;
  flex: 1;
  min-width: 160px;
  background: ${colors.neutral};
  border: 1px solid ${colors.neutralDark};
  border-radius: 10px;
  padding: 12px 14px;
`;

const SummaryIconBox = styled.div`
  display: flex;
  align-items: center;
  justify-content: center;
  width: 36px;
  height: 36px;
  border-radius: 8px;
  background: ${({ color }) => color};
  flex-shrink: 0;
`;

const SummaryInfo = styled.div`
  display: flex;
  flex-direction: column;
`;

const SummaryLabel = styled.span`
  font-size: ${fonts.sizes.small};
  color: ${colors.night};
`;

const SummaryValue = styled.span`
  font-size: ${fonts.sizes.highlight};
  font-weight: ${fonts.weight.bold};
  color: ${({ color }) => color || colors.nightDark};
`;

/* ── Calendar Overrides ────────────────────────────── */

const StyledCalendar = styled(ReactCalendar)`
  width: 100%;
  height: auto;
  border: 1px solid ${colors.neutralDark};
  border-radius: 10px;
  overflow: hidden;
  font-size: ${fonts.sizes.default};

  /* Navigation */
  .react-calendar__navigation {
    margin: 0;
    border-bottom: 1px solid ${colors.neutralDark};
  }

  .react-calendar__navigation button {
    min-width: 36px;
    background: none;

    &:hover,
    &:focus {
      background: ${colors.neutral};
    }
  }

  .react-calendar__navigation__label__labelText {
    font-size: ${fonts.sizes.subtitle};
    font-weight: ${fonts.weight.bold};
    color: ${colors.secondaryPurple};
    text-transform: capitalize;
  }

  .react-calendar__navigation__prev-button,
  .react-calendar__navigation__next-button {
    font-size: 24px;
    color: ${colors.secondaryPurple};
  }

  .react-calendar__navigation__prev2-button,
  .react-calendar__navigation__next2-button {
    display: none !important;
  }

  /* Weekday headers */
  .react-calendar__month-view__weekdays {
    color: ${colors.secondaryPurple};
    font-size: ${fonts.sizes.small};
    font-weight: ${fonts.weight.bold};
    text-transform: uppercase;
    padding: 8px 0;
    border-bottom: 1px solid ${colors.neutralDark};
    text-align: center;

    abbr {
      text-decoration: none;
    }
  }

  /* Tiles */
  .react-calendar__tile {
    position: relative;
    padding: 6px 4px;
    min-height: 70px;
    display: flex;
    flex-direction: column;
    align-items: center;
    border: 0.5px solid ${colors.neutralDark};
    transition: background .15s ease;

    @media screen and (min-width: ${breakpoints.sm}) {
      min-height: 80px;
    }

    @media screen and (min-width: ${breakpoints.md}) {
      min-height: 90px;
    }

    &:hover {
      background: ${colors.neutral};
    }
  }

  .react-calendar__tile abbr {
    font-weight: ${fonts.weight.bold};
    font-size: ${fonts.sizes.small};
  }

  /* Today */
  .react-calendar__tile--now {
    background: transparent;

    abbr {
      display: flex;
      align-items: center;
      justify-content: center;
      width: 26px;
      height: 26px;
      border-radius: 50%;
      background: ${colors.occasionalPurple};
      color: ${colors.neutralLight};
    }

    &:hover {
      background: ${colors.neutral};
    }
  }

  /* Active / selected */
  .react-calendar__tile--active {
    background: ${colors.neutral} !important;
    color: ${colors.night};
  }

  /* Neighboring months */
  .react-calendar__month-view__days__day--neighboringMonth {
    color: ${colors.nightLight} !important;
    opacity: 0.6;
  }

  .react-calendar__month-view__days__day--weekend {
    color: ${colors.nightDark};
  }
`;

/* ── Tile Content ──────────────────────────────────── */

const TileBody = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 4px;
  margin-top: 4px;
`;

const AmountBadge = styled.span`
  font-size: 10px;
  font-weight: ${fonts.weight.bold};
  padding: 2px 6px;
  border-radius: 10px;
  white-space: nowrap;
  background: ${({ isPast }) => (isPast ? "#e8f5e9" : "#fff3e0")};
  color: ${({ isPast }) => (isPast ? colors.success : colors.occasionalOrange)};
`;

const StatusDot = styled.span`
  display: inline-block;
  width: 8px;
  height: 8px;
  border-radius: 50%;
  background: ${({ isPast }) => (isPast ? colors.success : colors.occasionalOrange)};
  flex-shrink: 0;
`;

/* ── Legend ─────────────────────────────────────────── */

const Legend = styled(Row)`
  justify-content: center;
  gap: 20px;
  margin-top: 16px;
`;

const LegendItem = styled(Row)`
  align-items: center;
  gap: 6px;
`;
